import { createMachine } from 'xstate';

import shared from 'common/bento/shared';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { AnyMachineEvent } from 'features/Bento/types/Abstract';

import actions from './actions';
import guards, { Guard } from './guards';
import locales from './locales';
import model, { Context } from './model';
import services, { Service } from './services';

export enum State {
  INIT = 'init',

  PREPARE = 'prepare',
  UPLOAD = 'upload',
  SAVE = 'save',
  REVIEW = 'review',
  DELETE = 'delete',

  DONE = 'done',
  ABORT = 'abort',
}

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'capital-deposit-draft-statutes',

    initial: State.INIT,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
    },

    states: {
      [State.INIT]: {
        always: [
          {
            cond: Guard.IsDraftStatutesInvalid,
            target: State.PREPARE,
          },
          {
            cond: Guard.HasDraftStatutes,
            target: State.REVIEW,
          },
          {
            target: State.PREPARE,
          },
        ],
      },

      [State.PREPARE]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.UPLOAD,
          PREV: State.ABORT,
        },
      },

      [State.UPLOAD]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.PREPARE,
          SUBMIT_DOCUMENT_FILE: State.SAVE,
        },
      },

      [State.SAVE]: {
        invoke: {
          onDone: {
            actions: shared.actions.toast({
              status: 'success',
              title: locales.successMessage,
            }),
            target: State.REVIEW,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.UPLOAD,
          },
          src: Service.SendDocument,
        },
      },

      [State.REVIEW]: {
        entry: shared.actions.trackView(),

        on: {
          DELETE_DOCUMENT: State.DELETE,
          NEXT: State.DONE,
          PREV: State.ABORT,
        },
      },

      [State.DELETE]: {
        invoke: {
          onDone: {
            target: State.UPLOAD,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.INIT,
          },
          src: Service.DeleteDocument,
        },
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },
    },
  },

  {
    actions,
    guards,
    services,
  },
);

export default machine;
